import React, { useEffect } from 'react'
import { useSiteSettings } from '../hooks/useSiteSettings'
import { StructuredData } from '../lib/structured-data.tsx'
import { SITE_URL, OG_IMAGE_SIZE } from '../lib/site-url'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string[]
  image?: string
  /** Describes `image`. Omit it and no alt tags are emitted, which beats inventing one. */
  imageAlt?: string
  url?: string
  type?: 'website' | 'article'
  author?: string
  publishedTime?: string
  structuredData?: unknown
  noIndex?: boolean
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  imageAlt,
  url,
  type = 'website',
  author,
  publishedTime,
  structuredData,
  noIndex = false
}) => {
  const { settings } = useSiteSettings()

  useEffect(() => {
    // Update document title, prefixing the brand only when the page title lacks it
    const siteTitle = 'Demirix'
    if (!title) {
      document.title = siteTitle
    } else if (title.toLowerCase().includes(siteTitle.toLowerCase())) {
      document.title = title
    } else {
      document.title = `${siteTitle} | ${title}`
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement
      if (!meta) {
        meta = document.createElement('meta')
        meta.name = name
        document.head.appendChild(meta)
      }
      meta.content = content
    }

    const updatePropertyTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement
      if (!meta) {
        meta = document.createElement('meta')
        meta.setAttribute('property', property)
        document.head.appendChild(meta)
      }
      meta.content = content
    }

    // Set description
    const metaDescription = description || settings?.description || ''
    updateMetaTag('description', metaDescription)

    // Set keywords
    const metaKeywords = keywords?.join(', ') || settings?.keywords?.join(', ') || ''
    if (metaKeywords) {
      updateMetaTag('keywords', metaKeywords)
    }

    // Set canonical URL - auto-generate if not provided
    const getCanonicalUrl = () => {
      if (url) return url

      // Auto-generate canonical URL from current location, preferring non-www
      const currentUrl = new URL(window.location.href)
      const hostname = currentUrl.hostname.replace(/^www\./, '') // Remove www if present
      return `https://${hostname}${currentUrl.pathname}`
    }

    const canonicalUrl = getCanonicalUrl()
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
    if (!link) {
      link = document.createElement('link')
      link.rel = 'canonical'
      document.head.appendChild(link)
    }
    link.href = canonicalUrl

    // Additional meta tags
    updateMetaTag('robots', noIndex ? 'noindex, nofollow' : 'index, follow')
    updateMetaTag('author', author || 'Demirix')
    // theme-color is owned by ThemeContext: it has to follow the active theme,
    // and SEO runs per page, so setting it here would overwrite that on navigation.
    updateMetaTag('mobile-web-app-capable', 'yes')
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'default')

    // Open Graph tags
    updatePropertyTag('og:title', title || settings?.title || 'Demirix')
    updatePropertyTag('og:description', metaDescription)
    updatePropertyTag('og:type', type)
    updatePropertyTag('og:url', canonicalUrl)
    updatePropertyTag('og:locale', 'en_US')
    updatePropertyTag('og:site_name', 'Demirix')

    /*
     * The card is generated from mark.json by scripts/generate-og-image.mjs. That script
     * is where the reasoning lives: why it is the bare mark, why it is flat white rather
     * than appearance aware, and why it is a new filename rather than an overwrite.
     */
    const ogImage = image || `${SITE_URL}/og-card.png`
    updatePropertyTag('og:image', ogImage)

    /*
     * Declaring the dimensions is what stops the first share of a URL rendering without an
     * image. Meta's own guidance is to send width and height "so that it can render the
     * image immediately without having to asynchronously download and process it", and
     * without them the person who shares a new page first tends to get a blank card while
     * the crawler measures the file.
     *
     * Safe to state as a constant because every preview image in this project is 1200 by
     * 630, and src/test/lib/og-image.test.ts fails if any referenced image is not. That
     * matters: a wrong declared size is worse than none, since the consumer reserves the
     * wrong box and then paints into it.
     */
    updatePropertyTag('og:image:width', String(OG_IMAGE_SIZE.width))
    updatePropertyTag('og:image:height', String(OG_IMAGE_SIZE.height))

    /*
     * Alt text only when we can honestly write it.
     *
     * The default card is the DD mark, which is describable. A page passing its own image
     * knows what it depicts and this component does not, so when a custom image arrives
     * without imageAlt the tags are left off. An invented description is worse than
     * silence for anyone relying on a screen reader.
     */
    const resolvedImageAlt = image ? imageAlt : imageAlt || 'The Demirix logo mark'
    if (resolvedImageAlt) {
      updatePropertyTag('og:image:alt', resolvedImageAlt)
      updateMetaTag('twitter:image:alt', resolvedImageAlt)
    }

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image')
    updateMetaTag('twitter:site', '@Demirix_com')
    updateMetaTag('twitter:creator', '@Demirix_com')
    updateMetaTag('twitter:title', title || settings?.title || 'Demirix')
    updateMetaTag('twitter:description', metaDescription)
    updateMetaTag('twitter:image', ogImage)

    // Article specific tags
    if (type === 'article') {
      if (author) {
        updatePropertyTag('article:author', author)
      }
      if (publishedTime) {
        updatePropertyTag('article:published_time', publishedTime)
      }
    }

    // Cleanup function to reset title
    return () => {
      document.title = 'Demirix'
    }
  }, [
    title,
    description,
    keywords,
    image,
    imageAlt,
    url,
    type,
    author,
    publishedTime,
    settings,
    noIndex
  ])

  return (
    <>
      {structuredData && <StructuredData data={structuredData} />}
    </>
  )
}

export default SEO